import { useToast } from '../contexts/ToastContext';

/**
 * Custom hook with preset toast messages for common cycling app actions
 */
export const useCyclingToasts = () => {
  const toast = useToast();

  return {
    // Route-related toasts
    routeSaved: (routeName: string) =>
      toast.showSuccess(`Route "${routeName}" saved successfully`, {
        title: 'Route Saved',
        duration: 3000
      }),

    routeUpdated: (routeName: string) =>
      toast.showSuccess(`Route "${routeName}" updated successfully`, {
        title: 'Route Updated',
        duration: 3000
      }),

    routeDeleted: (routeName: string) =>
      toast.showInfo(`Route "${routeName}" deleted`, {
        title: 'Route Deleted',
        duration: 3000
      }),

    routeLoaded: (routeName: string) =>
      toast.showInfo(`Route "${routeName}" loaded`, {
        duration: 2000
      }),

    routeExported: (format: string = 'GPX') =>
      toast.showSuccess(`Route exported as ${format}`, {
        title: 'Export Successful',
        duration: 3000
      }),

    // Error toasts
    routeSaveError: (error?: string) =>
      toast.showError(error || 'Failed to save route. Please try again.', {
        title: 'Save Failed',
        duration: 5000
      }),

    routeLoadError: (error?: string) =>
      toast.showError(error || 'Failed to load route. Please try again.', {
        title: 'Load Failed',
        duration: 5000
      }),

    routeDeleteError: (error?: string) =>
      toast.showError(error || 'Failed to delete route. Please try again.', {
        title: 'Delete Failed',
        duration: 5000
      }),

    // Navigation/calculation toasts
    routeCalculating: () =>
      toast.showInfo('Calculating route...', {
        duration: 2000
      }),

    routeCalculated: (distance: string, duration: string) =>
      toast.showSuccess(`Route calculated: ${distance} km, ${duration}`, {
        title: 'Route Ready',
        duration: 4000
      }),

    routeCalculationError: (message?: string, title?: string) =>
      toast.showError(message || 'Failed to calculate route. Please check your waypoints.', {
        title: title || 'Calculation Failed',
        duration: 5000
      }),

    // Authentication toasts
    signInSuccess: () =>
      toast.showSuccess('Successfully signed in', {
        duration: 3000
      }),

    signOutSuccess: () =>
      toast.showInfo('Successfully signed out', {
        duration: 2000
      }),

    authError: (error: string) =>
      toast.showError(error, {
        title: 'Authentication Error',
        duration: 5000
      }),

    // General utility toasts
    copied: (item: string = 'Content') =>
      toast.showInfo(`${item} copied to clipboard`, {
        duration: 2000
      }),

    networkError: () =>
      toast.showError('Network error. Please check your connection.', {
        title: 'Connection Error',
        duration: 5000,
        persistent: true
      }),

    // Settings/preferences
    settingsSaved: () =>
      toast.showSuccess('Settings saved successfully', {
        duration: 2000
      }),

    profileChanged: (profileName: string) =>
      toast.showInfo(`Switched to ${profileName} profile`, {
        duration: 2000
      }),

    // Custom toast with action
    undoableAction: (message: string, onUndo: () => void) =>
      toast.showWarning(message, {
        duration: 8000,
        action: {
          label: 'UNDO',
          onClick: onUndo
        }
      }),

    // Direct access to base toast functions
    ...toast
  };
};
